import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "react-hot-toast";

import Header from "./components/Header";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import NoIndex from "./components/NoIndex";

/* Pages */
import Home from "./pages/Home";
import About from "./pages/About";
import PackagesNew from "./pages/PackagesNew";
import PackageDetailNew from "./pages/PackageDetailNew";
import Contact from "./pages/Contact";

/* Custom Package */
import CustomPackage from "./pages/CustomPackage";

/* Hotels */
import HotelsNew from "./pages/HotelsNew";
import HotelDetailNew from "./pages/HotelDetailNew";

/* Visa */
import VisaNew from "./pages/VisaNew";
import VisaDetailNew from "./pages/VisaDetailNew";

/* Cab */
import CabsNew from "./pages/CabsNew";

/* Cart */
import Cart from "./pages/Cart";

/* Auth/Admin */
import Login from "./pages/Login";

/* Quotations (public client-only link) */
import QuotationView from "./pages/QuotationView";

/* Vouchers (public client-only link) */
import VoucherView from "./pages/VoucherView";

/* Policies / Terms / Refund / Privacy */
import Policies from "./pages/Policies";

/* Checkout */
import Checkout from "./pages/Checkout";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentFailed from "./pages/PaymentFailed";

/* 404 */
import NotFound from "./pages/NotFound";

/* Floating WhatsApp */
import WhatsAppChat from "./components/WhatsAppChat";

/* Admin code is split into its own bundle so visitors never download it */
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminDashboardNew = lazy(() => import("./pages/AdminDashboardNew"));

/* Old duplicate routes now point at their canonical pages, so Google
   doesn't index the same content twice. */
function RedirectWithParam({ to, param }) {
  const params = useParams();
  return <Navigate to={`${to}/${params[param]}/`} replace />;
}

function App() {
  return (
    <div className="app-container">
      <Toaster position="top-right" />

      <Header />

      <main className="app-main">
        <Suspense fallback={<div className="container section">Loading…</div>}>
        <Routes>

          {/* Home */}
          <Route path="/" element={<Home />} />

          {/* About */}
          <Route path="/about" element={<About />} />

          {/* Packages */}
          <Route path="/packages" element={<PackagesNew />} />
          <Route path="/packages/:packageId" element={<PackageDetailNew />} />
          
          {/* Old Packages (Fallback) */}
          <Route path="/packages-old" element={<Navigate to="/packages/" replace />} />
          <Route path="/packages-old/:packageId" element={<RedirectWithParam to="/packages" param="packageId" />} />

          {/* Custom Package */}
          <Route path="/custom-package" element={<CustomPackage />} />

          {/* Hotels */}
          <Route path="/hotels" element={<HotelsNew />} />
          <Route path="/hotels/:hotelId" element={<HotelDetailNew />} />
          
          {/* Old Hotels (Fallback) */}
          <Route path="/hotels-old" element={<Navigate to="/hotels/" replace />} />
          <Route path="/hotels-old/:hotelId" element={<RedirectWithParam to="/hotels" param="hotelId" />} />

          {/* Visa */}
          <Route path="/visa" element={<VisaNew />} />
          <Route path="/visa/:id" element={<VisaDetailNew />} />
          
          {/* Old Visa (Fallback) */}
          <Route path="/visa-old" element={<Navigate to="/visa/" replace />} />
          <Route path="/visa-old/:visaId" element={<RedirectWithParam to="/visa" param="visaId" />} />

          {/* Cab */}
          <Route path="/cab" element={<CabsNew />} />
          
          {/* Old Cab (Fallback) */}
          <Route path="/cab-old" element={<Navigate to="/cab/" replace />} />
          <Route path="/cab/:cabId" element={<Navigate to="/cab/" replace />} />

          {/* Cart */}
          <Route path="/cart" element={<NoIndex title="Your Cart"><Cart /></NoIndex>} />
          
          {/* Old Cart (Fallback) */}
          <Route path="/cart-old" element={<Navigate to="/cart" replace />} />

          {/* Contact */}
          <Route path="/contact" element={<Contact />} />

          {/* Checkout */}
          <Route path="/checkout/:kind/:id" element={<NoIndex title="Checkout"><Checkout /></NoIndex>} />

          {/* Payment Results */}
          <Route path="/payment-success" element={<NoIndex title="Payment successful"><PaymentSuccess /></NoIndex>} />
          <Route path="/payment-failed" element={<NoIndex title="Payment failed"><PaymentFailed /></NoIndex>} />

          {/* Login */}
          <Route path="/login" element={<NoIndex title="Login"><Login /></NoIndex>} />

          {/* Quotations — client-only direct link, NOT linked anywhere public */}
          <Route path="/quotation/:slug" element={<QuotationView />} />
          {/* Backward-compat alias for older /quote/:slug links */}
          <Route path="/quote/:slug" element={<QuotationView />} />

          {/* Vouchers — client-only direct link, NOT linked anywhere public */}
          <Route path="/voucher/:slug" element={<VoucherView />} />

          {/* Policies / Terms / Refund / Privacy */}
          <Route path="/policies" element={<Policies />} />

          {/* 🔐 ADMIN (PROTECTED + FUTURE SAFE) */}
          <Route
            path="/admin/*"
            element={
              <NoIndex title="Admin">
                <ProtectedRoute requireAdmin>
                  <AdminDashboardNew />
                </ProtectedRoute>
              </NoIndex>
            }
          />

          {/* Old Admin (Fallback) */}
          <Route
            path="/admin-old/*"
            element={
              <NoIndex title="Admin">
                <ProtectedRoute requireAdmin>
                  <AdminDashboard />
                </ProtectedRoute>
              </NoIndex>
            }
          />

          {/* ❌ FALLBACK: real 404 page (noindex) instead of a duplicate Home */}
          <Route path="*" element={<NotFound />} />

        </Routes>
        </Suspense>
      </main>

      <Footer />

      <WhatsAppChat />
    </div>
  );
}

export default App;